import {
  columnAppliesToProductsOnly,
  columnCalcFromMetadata,
  columnCalcKind,
  FORMULA_BASIS_OPTIONS,
} from "./cogsColumnEdit";
import { columnAppliesRecipeIds } from "./cogsColumnProductScope";
import { isOverheadCogsColumn } from "./overheadCogsLink";
import type { MfgPricingLayerColumn } from "./cogsTypes";

export type ColumnSourceLink = {
  mode: "mfgLaborBilling" | "mfgOverhead";
  label: string;
};

export type ColumnSourceNote = {
  title: string;
  detail: string;
  formula: string | null;
  link: ColumnSourceLink | null;
};

export const MATERIALS_SOURCE_NOTE: ColumnSourceNote = {
  title: "Materials",
  detail:
    "This is the saved materials total from the product's bill of materials. The first visit calculates and saves it. Later visits read that total. Use the clock on this page to recalculate.",
  formula: null,
  link: null,
};

function money(rate: number): string {
  const text = String(rate);
  return text.startsWith("$") ? text : `$${text}`;
}

/** Sentence for a formula stored on the column, including a picked product list. */
export function columnFormulaSentence(column: {
  source_module: string | null;
  key: string;
  metadata?: unknown;
}): string | null {
  const calc = columnCalcFromMetadata(column.metadata);
  if (!calc) return null;
  const ids = columnAppliesRecipeIds(column.metadata);
  const productsOnly = columnAppliesToProductsOnly(column);
  if (calc.basis === "none") {
    return ids
      ? "The saved formula is $0. Every other product is $0 too."
      : "The saved formula is $0.";
  }
  const option = FORMULA_BASIS_OPTIONS.find((item) => item.id === calc.basis);
  const amount =
    calc.basis === "flat_usd"
      ? `${money(calc.rate_usd)} each`
      : calc.basis === "building_sq_ft"
        ? `${money(calc.rate_usd)} per sq ft`
        : calc.basis === "building_length"
          ? `${money(calc.rate_usd)} per ft`
          : `${money(calc.rate_usd)} ${option?.rateLabel ?? ""}`.trim();
  if (ids) {
    const count = ids.length === 1 ? "1 product" : `${ids.length} products`;
    return `${amount} on ${count}. Every other product is $0.`;
  }
  if (productsOnly) return `${amount} on products. Options and packages are $0.`;
  return `${amount} on products, options, and packages.`;
}

function isLaborColumn(column: { key: string; source_module: string | null }): boolean {
  return column.key === "labor" || column.source_module === "labor";
}

function isOverheadColumn(column: MfgPricingLayerColumn): boolean {
  return column.key === "overhead" || column.source_module === "overhead" || isOverheadCogsColumn(column);
}

export function columnSourceNote(column: MfgPricingLayerColumn): ColumnSourceNote {
  if (isLaborColumn(column)) {
    return {
      title: "Labor rates",
      detail:
        "Filled in Manufacturing → Labor rates. This column adds the company labor rates matched to the product. Open that mode to see the rates.",
      formula: null,
      link: { mode: "mfgLaborBilling", label: "Open Labor rates" },
    };
  }
  if (isOverheadColumn(column)) {
    return {
      title: "Overhead",
      detail:
        "Calculated in Manufacturing → Overhead and written onto this column. Open Overhead to change the rate or the basis.",
      formula: null,
      link: { mode: "mfgOverhead", label: "Open Overhead" },
    };
  }

  const formula = columnFormulaSentence(column);
  const kind = columnCalcKind(column);
  if (formula) {
    return {
      title: "Calculated column",
      detail: "Calculated on this column. Open Columns and choose Change existing to edit the formula.",
      formula,
      link: null,
    };
  }
  if (kind === "margin_denom") {
    const onCogs = column.source_kind === "band_total" && column.source_band === "cogs";
    return {
      title: "Calculated margin",
      detail: onCogs
        ? "Each row stores the margin fraction. 0.12 means 12% of the wholesale price. Change the fraction on the row, or set many rows with Bulk edit."
        : "Each row stores the margin fraction. 0.15 means 15% of the price after this column. Change the fraction on the row, or set many rows with Bulk edit.",
      formula: onCogs
        ? "Wholesale price = COGS total ÷ (1 − margin)."
        : "Price = running total ÷ (1 − margin).",
      link: null,
    };
  }
  if (kind === "markup") {
    return {
      title: "Calculated markup",
      detail:
        "Each row stores the markup fraction. 0.15 means 15% added on top. Change the fraction on the row, or set many rows with Bulk edit.",
      formula: "Price = amount underneath × (1 + markup).",
      link: null,
    };
  }
  if (kind === "multiply") {
    return {
      title: "Calculated multiply",
      detail: "Each row stores the factor. Change it on the row, or set many rows with Bulk edit.",
      formula: "This column multiplies the amount underneath by the factor on the row.",
      link: null,
    };
  }
  if (column.source_module === "plant_haul" || column.key === "plant_haul") {
    return {
      title: "Calculated column",
      detail:
        "Plant haul is calculated on this column. No formula is saved yet, so each row is using the dollar stored there. Set the formula under Columns → Change existing.",
      formula: null,
      link: null,
    };
  }
  if (column.source_module === "lot_haul" || column.key === "lot_haul") {
    return {
      title: "Calculated column",
      detail:
        "Lot haul is calculated on this column. No formula is saved yet, so each row is using the dollar stored there. Set the formula under Columns → Change existing.",
      formula: null,
      link: null,
    };
  }
  return {
    title: "Typed amount",
    detail: "Each row stores its own dollar, and this column adds that dollar. It is not filled from another screen.",
    formula: null,
    link: null,
  };
}
